import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  Clock,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Shirt,
  BookOpen,
  Box,
  Calculator,
  Scale,
  Sparkles,
  AlertCircle,
  Home,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Aigremont ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de fournitures de déménagement
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous assurons un service logistique de proximité pour
              vous livrer dans les meilleures conditions.
            </p>

            {/* Callout sécurité */}
            <div className="mt-6 flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5">
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#FF5722] text-white">
                <AlertCircle className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  Le réflexe sécurité
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  Vos objets précieux et vos souvenirs méritent mieux
                  que des cartons de récupération fragilisés. Offrez-leur
                  la solidité de fournitures conçues pour résister au
                  transport et à l&apos;empilement.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Zone de livraison",
                body: "Nous desservons tout le territoire d'Aigremont ainsi que les villes voisines des Yvelines comme Poissy, Chambourcy, Saint-Germain-en-Laye et Feucherolles.",
                Icon: MapPin,
              },
              {
                title: "Souplesse et rapidité",
                body: "Commandez en quelques clics et planifiez la réception de vos cartons directement à votre domicile selon vos disponibilités.",
                Icon: Clock,
              },
              {
                title: "Praticité",
                body: "Vos cartons vous sont livrés à plat, propres et parfaitement protégés contre l'humidité.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — 3 astuces de pro ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Astuces de pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos astuces de pro pour votre déménagement {city.aCity}
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              num: "01",
              title: "Anticipez le volume",
              body: "On a souvent tendance à sous-estimer le nombre de cartons nécessaires. Prendre un pack supérieur vous évite de vous retrouver à court la veille du départ.",
              Icon: Calculator,
              accent: "#237cb5",
            },
            {
              num: "02",
              title: "Équilibrez les poids",
              body: "Placez les éléments les plus lourds au fond du carton et comblez les espaces vides avec du papier bulle ou du linge de maison pour éviter que le contenu ne bouge.",
              Icon: Scale,
              accent: "#237cb5",
            },
            {
              num: "03",
              title: "Marquez tout",
              body: 'Indiquez clairement "Fragile" sur les cartons contenant de la vaisselle ou des objets délicats pour guider les déménageurs.',
              Icon: Tag,
              accent: "#FF5722",
            },
          ].map(({ num, title, body, Icon, accent }) => (
            <div
              key={num}
              className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 overflow-hidden"
            >
              <span
                className="absolute top-0 left-0 h-1.5 w-full"
                style={{ backgroundColor: accent }}
              />
              <div className="flex items-baseline gap-3">
                <span
                  className="font-display font-extrabold text-[40px] leading-none"
                  style={{ color: accent }}
                >
                  {num}
                </span>
                <span
                  className="flex items-center justify-center h-10 w-10 rounded-xl text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
              </div>
              <h3 className="mt-5 font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Packs et fournitures ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Solutions modulables
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des packs et fournitures adaptés à tous les volumes
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que chaque habitation est unique, nous vous proposons
            des solutions modulables pour emballer efficacement tout
            votre mobilier.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Packs Clé en Main */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Home className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Packs Clé en Main
              </h3>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Studio, Appartement, Maison : des assortiments
              soigneusement calculés comprenant des cartons standards,
              des cartons pour livres, du ruban adhésif et du papier
              bulle pour ne manquer de rien.
            </p>
            <Link
              to="/kits-cartons"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[13px] transition-colors"
              data-testid="city-aigremont-cta-kits"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir les kits
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          {/* Cartons spécifiques */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Box className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Cartons spécifiques à l&apos;unité
              </h3>
            </div>
            <ul className="mt-4 space-y-3 text-[13.5px] text-[#3a4856]">
              <li className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 mt-0.5 text-[#237cb5] shrink-0" strokeWidth={2.6} />
                <span><strong className="text-[#0f1d2b]">Standards &amp; renforcés</strong> — linge, électroménager et quotidien.</span>
              </li>
              <li className="flex items-start gap-2">
                <Shirt className="h-4 w-4 mt-0.5 text-[#237cb5] shrink-0" strokeWidth={2.6} />
                <span><strong className="text-[#0f1d2b]">Cartons penderie</strong> — tringle intégrée pour vêtements sur cintres.</span>
              </li>
              <li className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 mt-0.5 text-[#237cb5] shrink-0" strokeWidth={2.6} />
                <span><strong className="text-[#0f1d2b]">Cartons livres</strong> — format adapté aux charges denses.</span>
              </li>
            </ul>
            <Link
              to="/cartons-demenagement"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[13px] transition-colors"
              data-testid="city-aigremont-cta-cartons"
            >
              <Package className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          {/* Protection */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Layers className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Matériel de protection
              </h3>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Rouleaux de film à bulles haute protection, film
              étirable, dévidoirs et adhésifs robustes pour
              sécuriser l&apos;intégralité de votre commande.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/emballage-protection"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-aigremont-chip-bulles"
              >
                Film à bulles
              </Link>
              <Link
                to="/emballage-protection"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-aigremont-chip-etirable"
              >
                Film étirable
              </Link>
              <Link
                to="/fermeture-accessoires"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-aigremont-chip-devidoir"
              >
                Dévidoirs
              </Link>
              <Link
                to="/fermeture-accessoires"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-aigremont-chip-adhesif"
              >
                Adhésifs robustes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-aigremont-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
